import { useState, useEffect, useCallback } from 'react'
import { type UserPreferences } from '@/services/userService'
import type { SportModality } from '@/types/address'
import { modalitiesManager } from '@/managers/modalitiesManager'
import { userManager } from '@/managers/userManager'
import { appPreferencesManager } from '@/managers/appPreferencesManager'

interface UseUserPreferencesOptions {
  /**
   * Whether to automatically load preferences on mount
   * @default true
   */
  autoLoad?: boolean
  /**
   * Whether user is authenticated (for backend sync)
   * @default false
   */
  isAuthenticated?: boolean
}

interface UseUserPreferencesReturn {
  preferences: UserPreferences | null
  isLoading: boolean
  error: Error | null
  savePreferences: (modalities: SportModality[]) => Promise<void>
  refreshPreferences: () => Promise<void>
  shouldShowModal: boolean
  markModalAsShown: () => void
}

export function useUserPreferences(
  options: UseUserPreferencesOptions = {}
): UseUserPreferencesReturn {
  const { autoLoad = true, isAuthenticated = false } = options

  const [preferences, setPreferences] = useState<UserPreferences | null>(null)
  const [isLoading, setIsLoading] = useState(autoLoad)
  const [error, setError] = useState<Error | null>(null)
  const [shouldShowModal, setShouldShowModal] = useState(false)

  // Load preferences from storage (using userManager)
  const loadFromLocalStorage = useCallback((): UserPreferences | null => {
    return userManager.getPreferences()
  }, [])

  // Save preferences to storage (using userManager)
  const saveToLocalStorage = useCallback((prefs: UserPreferences) => {
    userManager.savePreferences(prefs)
  }, [])

  // Check if preferences modal has been shown before (using appPreferencesManager)
  const hasShownModal = useCallback((): boolean => {
    return appPreferencesManager.hasShownPreferencesModal()
  }, [])

  // Mark modal as shown (using appPreferencesManager)
  const markModalAsShown = useCallback(() => {
    appPreferencesManager.markPreferencesModalAsShown()
    setShouldShowModal(false)
  }, [])

  // Load preferences (from localStorage + optional backend sync)
  const loadPreferences = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Always load from localStorage first
      const localPrefs = loadFromLocalStorage()

      if (localPrefs && localPrefs.modalities && localPrefs.modalities.length > 0) {
        // Has preferences saved
        setPreferences(localPrefs)
        setShouldShowModal(false)
      } else {
        // No preferences - should show modal if never shown
        setPreferences(null)
        if (!hasShownModal()) {
          setShouldShowModal(true)
        }
      }

      // Backend sync disabled for now
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load preferences')
      setError(error)
      console.error('[useUserPreferences] Error loading preferences:', err)
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated, loadFromLocalStorage, hasShownModal])

  // Save preferences (always to localStorage, optionally to backend if authenticated)
  const savePreferences = useCallback(async (modalities: SportModality[]) => {
    try {
      setIsLoading(true)
      setError(null)

      const modalityIds = modalities.map(m => m.id)
      const prefs: UserPreferences = { modalities: modalityIds }

      // Always save to localStorage
      saveToLocalStorage(prefs)
      setPreferences(prefs)
      markModalAsShown()

      // Backend sync disabled for now
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to save preferences')
      setError(error)
      console.error('[useUserPreferences] Error saving preferences:', err)
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated, saveToLocalStorage, markModalAsShown])

  // Refresh preferences (force reload from API)
  const refreshPreferences = useCallback(async () => {
    await loadPreferences()
  }, [loadPreferences])

  // Load preferences on mount if autoLoad is true
  useEffect(() => {
    if (autoLoad) {
      loadPreferences()
    }
  }, [autoLoad, loadPreferences])

  return {
    preferences,
    isLoading,
    error,
    savePreferences,
    refreshPreferences,
    shouldShowModal,
    markModalAsShown
  }
}

/**
 * Hook to get modality objects from preference IDs
 * Useful for displaying modality names/details
 */
export function usePreferenceModalities(preferenceIds: string[] | undefined) {
  const [modalities, setModalities] = useState<SportModality[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadModalities = async () => {
      if (!preferenceIds || preferenceIds.length === 0) {
        setModalities([])
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        await modalitiesManager.loadModalities()

        const modalitiesData: SportModality[] = []
        for (const id of preferenceIds) {
          const modality = modalitiesManager.getModalityById(id)
          if (modality) {
            modalitiesData.push({
              id: modality.id,
              name: modality.description
            })
          }
        }

        setModalities(modalitiesData)
      } catch (error) {
        console.error('[usePreferenceModalities] Error loading modalities:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadModalities()
  }, [preferenceIds])

  return { modalities, isLoading }
}
